/**
 * useViewMenuActions — Context-aware menu actions for the ViewHeader "..." menu.
 *
 * Returns actions adapted to the current view type (Things 3 pattern):
 * - New Project: Today, Someday, Anyday, Area views
 * - Paste: All views (clipboard text → new task)
 * - Share: All views (share view URL via OS sheet / clipboard)
 */

import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useViewContext } from "@/app/stores/viewContext"
import { addProject } from "@/db/operations"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { pasteTasksFromClipboard } from "@/shared/utils/task/pasteTasks"
import { shareContent, getAppUrl } from "@/shared/utils/platform/share"
import { ROUTES } from "@/config/routes"
import type { ViewHeaderAction } from "../components/ViewHeader"
import type { ViewId } from "../viewRegistry"

// Icons
import PlusCircleIcon from "@/shared/icons/pikaicons/stroke/maths/plus-circle.svg?react"
import CopyIcon from "@/shared/icons/pikaicons/stroke/general/copy-default.svg?react"
import ShareIcon from "@/shared/icons/totodoicons/stroke/share.svg?react"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Views where "New Project" makes sense (user can organize tasks into projects) */
const NEW_PROJECT_VIEWS: ViewId[] = ["today", "someday", "anyday", "area"]

/** Views where "Paste" makes sense (user can add tasks) */
const PASTE_VIEWS: ViewId[] = [
  "today",
  "evening",
  "upcoming",
  "someday",
  "anyday",
  "inbox",
  "project",
  "list",
  "area",
]

/** Map viewId to shareable route */
function getViewRoute(viewId: ViewId, containerId: string | null): string {
  switch (viewId) {
    case "today":
      return ROUTES.TODAY
    case "upcoming":
      return ROUTES.UPCOMING
    case "someday":
      return ROUTES.SOMEDAY
    case "anyday":
      return ROUTES.ANYDAY
    case "inbox":
      return ROUTES.INBOX
    case "logbook":
      return ROUTES.LOGBOOK
    case "project":
      return containerId ? ROUTES.PROJECT(containerId) : "/"
    case "list":
      return containerId ? ROUTES.LIST(containerId) : "/"
    case "area":
      return containerId ? ROUTES.AREA(containerId) : "/"
    default:
      return "/"
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useViewMenuActions(): ViewHeaderAction[] {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const viewId = useViewContext((s) => s.viewId)
  const containerId = useViewContext((s) => s.containerId)
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)

  // --- New Project ---
  const handleNewProject = useCallback(async () => {
    const result = await addProject("")
    if (!result.ok) return
    useEditFocusStore.getState().requestEditFocus(result.data.id)
    navigate(ROUTES.PROJECT(result.data.id))
  }, [navigate])

  // --- Paste ---
  const handlePaste = useCallback(async () => {
    // Each line becomes a task (Things 3 pattern); shared, atomic paste path
    await pasteTasksFromClipboard({ viewId, containerId, eveningHour })
  }, [viewId, containerId, eveningHour])

  // --- Share ---
  const handleShare = useCallback(async () => {
    const route = getViewRoute(viewId, containerId)
    const url = getAppUrl(route)
    await shareContent({
      title: "ToToDo",
      url,
    })
  }, [viewId, containerId])

  // --- Build actions array ---
  return useMemo(() => {
    const actions: ViewHeaderAction[] = []

    // New Project
    if (NEW_PROJECT_VIEWS.includes(viewId)) {
      actions.push({
        label: t("New Project"),
        icon: <PlusCircleIcon className="size-4" />,
        onClick: handleNewProject,
      })
    }

    // Paste
    if (PASTE_VIEWS.includes(viewId)) {
      actions.push({
        label: t("Paste"),
        icon: <CopyIcon className="size-4" />,
        shortcut: "mod+v",
        onClick: handlePaste,
      })
    }

    // Share
    actions.push({
      label: t("Share"),
      icon: <ShareIcon className="size-4" />,
      onClick: handleShare,
    })

    return actions
  }, [viewId, handleNewProject, handlePaste, handleShare, t])
}
